<template>
  <section class="sales-impact-totals" aria-label="Sales impact totals">
    <div class="totals-header">
      <div>
        <span class="eyebrow">Report Snapshot</span>
        <h3>Sales Impact Totals</h3>
      </div>
      <span class="lead-count">{{ leadCountLabel }}</span>
    </div>

    <div class="metric-grid">
      <div class="metric-card primary">
        <span class="metric-label">Total Order Value</span>
        <strong>{{ formattedTotalOrderValue }}</strong>
      </div>
      <div class="metric-card">
        <span class="metric-label">Total Interactions</span>
        <strong>{{ formattedTotalInteractions }}</strong>
      </div>
      <div class="metric-card">
        <span class="metric-label">Closed Leads</span>
        <strong>{{ formattedClosedLeads }}</strong>
      </div>

      <div class="value-rate-card">
        <div class="close-rate-header">
          <span>Closed Lead Outcome</span>
          <strong>{{ formattedClosedValueRate }} converted</strong>
        </div>
        <div class="rate-bar" aria-hidden="true">
          <span class="rate-bar-fill" :style="{ width: formattedClosedValueRate }"></span>
        </div>
        <div class="rate-breakdown">
          <span>{{ formattedClosedValueRate }} converted to an order</span>
          <span>{{ formattedClosedNoValueRate }} closed without an order</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'SalesImpactReportTotals',
  props: {
    leads: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    totalLeads() {
      return this.leads.length;
    },
    closedLeads() {
      return this.leads.filter((lead) => lead.COMPLETE);
    },
    closedLeadsWithValue() {
      return this.closedLeads.filter((lead) => this.parseAmount(lead.AMOUNT) > 0);
    },
    totalOrderValue() {
      return this.closedLeadsWithValue.reduce((total, lead) => total + this.parseAmount(lead.AMOUNT), 0);
    },
    totalInteractions() {
      return this.leads.reduce((total, lead) => total + this.parseNumber(lead.TOTALINTERACTIONS), 0);
    },
    closedNoValueCount() {
      return this.closedLeads.length - this.closedLeadsWithValue.length;
    },
    closedValueRate() {
      return this.getPercentage(this.closedLeadsWithValue.length, this.closedLeads.length);
    },
    closedNoValueRate() {
      return this.getPercentage(this.closedNoValueCount, this.closedLeads.length);
    },
    formattedTotalLeads() {
      return this.formatNumber(this.totalLeads);
    },
    leadCountLabel() {
      return `${this.formattedTotalLeads} ${this.totalLeads === 1 ? 'lead' : 'leads'}`;
    },
    formattedTotalOrderValue() {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(this.totalOrderValue);
    },
    formattedTotalInteractions() {
      return this.formatNumber(this.totalInteractions);
    },
    formattedClosedLeads() {
      return this.formatNumber(this.closedLeads.length);
    },
    formattedClosedValueRate() {
      return this.formatPercentage(this.closedValueRate);
    },
    formattedClosedNoValueRate() {
      return this.formatPercentage(this.closedNoValueRate);
    },
  },
  methods: {
    parseAmount(value) {
      if (value === null || value === undefined || value === '') return 0;
      const amount = parseFloat(String(value).replace(/[$,]/g, ''));
      return Number.isFinite(amount) ? amount : 0;
    },
    parseNumber(value) {
      if (value === null || value === undefined || value === '') return 0;
      const number = parseInt(value, 10);
      return Number.isFinite(number) ? number : 0;
    },
    getPercentage(part, total) {
      return total > 0 ? (part / total) * 100 : 0;
    },
    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value);
    },
    formatPercentage(value) {
      return `${value.toFixed(1)}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.sales-impact-totals {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  text-align: left;
}

.totals-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  h3 {
    font-size: $fontSize24;
    font-weight: 400;
    color: $textColor87;
    margin: 0.1rem 0 0;
  }
}

.eyebrow,
.metric-label,
.lead-count,
.rate-breakdown {
  color: $textColor60;
  font-size: $fontSize11;
}

.eyebrow,
.metric-label {
  text-transform: uppercase;
  letter-spacing: 0.06rem;
  font-weight: 700;
}

.lead-count {
  padding: 0.45rem 0.8rem;
  border-radius: $roundedBorderRadius;
  background: #e9edf1;
  color: $primaryActionColor;
  font-weight: 700;
  white-space: nowrap;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.55rem;
  min-height: 5.5rem;
  padding: 1rem 1.25rem;
  border-left: 3px solid $textColor12;
  border-radius: $borderRadius5;
  background: rgba(255, 255, 255, 0.74);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);

  strong {
    color: $textColor87;
    font-size: $fontSize24;
    font-weight: 500;
    line-height: 1;
  }

  &.primary {
    border-left-color: $primaryActionColor;

    strong {
      color: $primaryActionColor;
    }
  }
}

.value-rate-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.7rem;
  min-height: 5.5rem;
  padding: 1rem 1.25rem;
  border-left: 3px solid $secondaryActionColor;
  border-radius: $borderRadius5;
  background: rgba(255, 255, 255, 0.74);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.close-rate-header,
.rate-breakdown {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.close-rate-header {
  color: $textColor87;
  font-size: $fontSize14;

  strong {
    color: $primaryActionColor;
  }
}

.rate-bar {
  height: 0.55rem;
  overflow: hidden;
  border-radius: $roundedBorderRadius;
  background: $textColor12;
}

.rate-bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, $primaryActionColor, $secondaryActionColor);
  transition: width 0.2s ease;
}

.rate-breakdown {
  flex-wrap: wrap;
}

@media (max-width: 1100px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
